import React, { Fragment, useState, useEffect, useMemo } from 'react'
import classnames from 'classnames'
import {
  makeStyles,
  CircularProgress,
  Typography,
  IconButton,
  OutlinedInput,
} from '@material-ui/core'
import { CameraIcon } from '../../../Assets/Images/icons/camera'
import { roleLabel } from '../../UserManagement/UsersListTable'
import { styles } from './styles'
import {
  validateUserProfile,
  PROFILE_FORM_INPUTS,
  handleImageUpdate,
  requiredFields,
} from './utils'
import { ROLE_CLIENT, ROLE_CLIENT_ADMIN } from '../../../shared/constants'
import classNames from 'classnames'
import { getReadableRoleByUser } from '../../UserManagement/UserDetails/utils'

const useStyles = makeStyles(() => styles)

export const ProfileEditForm = ({
  user,
  setEdit,
  edit,
  handleSave,
  saving,
}) => {
  const classes = useStyles()
  const loading = !Object.values(user).length
  const [userData, setUserData] = useState({})
  const [errors, setErrors] = useState({})
  const [profilePicture, setProfilePicture] = useState(user.profilePicture)

  const disableSave = useMemo(
    () =>
      !requiredFields.every(inputName => !!userData[inputName]) ||
      Object.values(errors).some(value => !!value),
    [errors, userData],
  )

  const hiddenFileInput = React.useRef(null)

  useEffect(() => {
    if (!loading) {
      setUserData({ ...user })
    }
  }, [user, loading])

  const handleImageButtonClick = event => {
    hiddenFileInput.current.click()
  }

  const handleFormChange = e => {
    validateUserProfile(e, setErrors, errors)
    setUserData({ ...userData, [e.target.name]: e.target.value })
  }

  return loading || saving ? (
    <div className={classes.loadingContainer}>
      <CircularProgress />
    </div>
  ) : (
    <Fragment>
      <div
        className={classNames(
          classes.infoContainer,
          'makeStyles-infoContainer',
        )}
      >
        <div
          className={classNames(classes.imageColumn, 'makeStyles-imageColumn')}
        >
          <div className={classes.imageContainer}>
            <img
              className={classNames(classes.image, 'makeStyles-image')}
              src={profilePicture}
              alt="profile"
            />
            <IconButton
              className={classes.cameraIcon}
              onClick={handleImageButtonClick}
            >
              <CameraIcon />
              <input
                type="file"
                accept=".jpg, .jpeg, .png"
                ref={hiddenFileInput}
                onChange={event =>
                  handleImageUpdate({
                    event,
                    setUserData,
                    userData,
                    setProfilePicture,
                  })
                }
                style={{ display: 'none' }}
              />
            </IconButton>
          </div>
        </div>
        <div className={classes.info}>
          <div className={classnames(classes.row, 'makeStyles-row')}>
            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            >
              <Typography className={classes.infoItemTitle}>
                First name
              </Typography>
              <OutlinedInput
                value={userData.firstName}
                name={PROFILE_FORM_INPUTS.FIRST_NAME}
                onChange={e => handleFormChange(e)}
                error={!!errors[PROFILE_FORM_INPUTS.FIRST_NAME]}
              />
              {errors[PROFILE_FORM_INPUTS.FIRST_NAME] && (
                <span className={classes.error}>
                  {errors[PROFILE_FORM_INPUTS.FIRST_NAME]}
                </span>
              )}
            </div>
            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            >
              <Typography className={classes.infoItemTitle}>
                Last name
              </Typography>
              <OutlinedInput
                value={userData.lastName}
                name={PROFILE_FORM_INPUTS.LAST_NAME}
                onChange={e => handleFormChange(e)}
                error={!!errors[PROFILE_FORM_INPUTS.LAST_NAME]}
              />
              {errors[PROFILE_FORM_INPUTS.LAST_NAME] && (
                <span className={classes.error}>
                  {errors[PROFILE_FORM_INPUTS.LAST_NAME]}
                </span>
              )}
            </div>
            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            >
              <Typography className={classes.infoItemTitle}>Role</Typography>
              <Typography>
                {roleLabel[getReadableRoleByUser(userData)]}
              </Typography>
            </div>
          </div>

          <div className={classnames(classes.row, 'makeStyles-row')}>
            {[ROLE_CLIENT_ADMIN, ROLE_CLIENT].includes(user.role) ? null : (
              <div
                className={classnames(classes.infoItem, 'makeStyles-infoItem')}
              >
                <Typography className={classes.infoItemTitle}>
                  Country
                </Typography>
                <Typography>{userData.countryName}</Typography>
              </div>
            )}
            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            >
              <Typography className={classes.infoItemTitle}>Company</Typography>
              <OutlinedInput
                value={(userData.client && userData.client.name) || '-'}
                disabled
              />
            </div>
            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            />
          </div>

          <div className={classnames(classes.row, 'makeStyles-row')}>
            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            >
              <Typography className={classes.infoItemTitle}>
                Email address
              </Typography>
              <OutlinedInput
                value={userData.email}
                name={PROFILE_FORM_INPUTS.EMAIL}
                onChange={e => handleFormChange(e)}
                error={!!errors[PROFILE_FORM_INPUTS.EMAIL]}
                disabled
              />
              {errors[PROFILE_FORM_INPUTS.EMAIL] && (
                <span className={classes.error}>
                  {errors[PROFILE_FORM_INPUTS.EMAIL]}
                </span>
              )}
            </div>
            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            >
              <Typography className={classes.infoItemTitle}>
                Business phone number
              </Typography>
              <OutlinedInput
                value={userData.mobilePhone}
                name={PROFILE_FORM_INPUTS.PHONE}
                onChange={e => handleFormChange(e)}
                error={!!errors[PROFILE_FORM_INPUTS.PHONE]}
              />
              {errors[PROFILE_FORM_INPUTS.PHONE] && (
                <span className={classes.error}>
                  {errors[PROFILE_FORM_INPUTS.PHONE]}
                </span>
              )}
            </div>
            <div
              className={classnames(classes.infoItem, 'makeStyles-infoItem')}
            />
          </div>
        </div>
      </div>
      <div className={classes.buttonsContainer}>
        <button
          className={classes.profileButton}
          onClick={() => {
            setEdit(!edit)
          }}
          cypress_id={'profileBtn'}
        >
          Cancel
        </button>
        <button
          className={classnames(classes.profileButton, classes.saveButton, {
            [classes.disabled]: disableSave,
          })}
          onClick={() => {
            handleSave(userData)
          }}
          disabled={disableSave}
          cypress_id={'profileBtn'}
        >
          Save
        </button>
      </div>
    </Fragment>
  )
}
